import { action } from '@ember/object';
import { and, empty, notEmpty } from '@ember/object/computed';
import { classNameBindings, classNames } from '@ember-decorators/component';
import { isEmpty } from '@ember/utils';
import { next } from '@ember/runloop';
import Component from '@ember/component';
import DestinationPortalMixin from './typeahead/destination-portal';
import IsAnimatedMixin from '../../mixins/component/is-animated';
import TypeaheadDestination from './typeahead/_destination.gjs';
import classic from 'ember-classic-decorator';
import styles from './typeahead.module.scss';

@classic
@classNameBindings('isAnimated', 'isOpen', 'isDestinationAbove')
@classNames(styles.component)
export default class SharedTypeahead extends Component.extend(
  IsAnimatedMixin,
  DestinationPortalMixin
) {
  TypeaheadDestination = TypeaheadDestination;
  styles = styles;
  optionName = 'name';
  searchMinChars = 1;
  searchDelayTime = 300;
  triggerPlaceholder = 'Select';
  inputPlaceholder = '';
  inputValue = '';
  onInputChange = null;
  onSelect = null;
  isCachingEnabled = true;
  useAsInputField = false;

  query = '';
  isSearching = false;
  hasNoResults = false;
  options = [];
  initialOptionsCache = [];
  isOpen = false;
  isInfinity = false;

  @notEmpty('initialOptionsCache') hasInitialOptionsCache;
  @empty('query') isEmptyQuery;
  @and('isCachingEnabled', 'isEmptyQuery', 'hasInitialOptionsCache') isCacheHit;

  @action
  handleSelect(value, sb) {
    this._focusElement(sb);
    this.onSelect?.(value, sb);
    sb.close();
  }

  @action
  handleOpen(sb) {
    this.set('isOpen', true);
    this._focusInput(sb);
    sb.search(this.useAsInputField ? this.inputValue : '');
    this.onOpen?.(sb);
    this._activateDestinationPortal(sb);
  }

  @action
  handleClose(sb) {
    this.set('isOpen', false);
    sb.deactivateOptions();
    if (!this.useAsInputField) {
      sb.setInputValue('');
    }
    this.set('query', '');
    this._deactivateDestinationPortal();
    this.onClose?.(sb);
  }

  @action
  handleClear(sb) {
    this.set('query', '');
    this._restoreOptionsFromCache(sb);
  }

  @action
  async handleSearch(query, sb) {
    let options = [];
    let isSearching = true;
    let hasNoResults = false;

    this.setProperties({ isSearching, hasNoResults, query });

    try {
      options = await this._search(sb);
    } catch {
      // noop
    }

    return options;
  }

  @action
  handleSearched(options, _query, _sb) {
    let hasNoResults = isEmpty(options);
    let isSearching = false;

    if (this.isEmptyQuery) {
      this._setInitialOptionsCache(options);
    }

    this.setProperties({ options, hasNoResults, isSearching });
    this.onSearched?.(...arguments);
  }

  @action
  handlePressUp(evt, sb) {
    evt.preventDefault();
    this._focusElement(sb);
    sb.activatePreviousOption();
  }

  @action
  handlePressDown(evt, sb) {
    evt.preventDefault();
    this._focusElement(sb);
    sb.activateNextOption();
    sb.open();
  }

  @action
  handlePressEnter(_evt, sb) {
    if (!sb.isOpen) {
      sb.open();
    }
  }

  @action
  handlePressEscape(_evt, sb) {
    this._focusElement(sb);
    sb.close();
  }

  @action
  handleFocusLeave(evt, sb) {
    if (
      this._isInsideDestinationMenu(evt.relatedTarget) ||
      this._isInsideDestinationMenu(document.activeElement)
    ) {
      return;
    }

    if (sb.isOpen && !evt.relatedTarget) {
      this._focusElement(sb);

      if (this.destinationElement) {
        return;
      }
    }

    this._notifyFocusLeave(sb, evt);
    sb.close();
  }

  @action
  handleDestinationFocusLeave(sb, evt) {
    this._handleDestinationFocusLeave(sb, evt);
  }

  @action
  handleDestinationFocusEnter() {
    this._armFocusLeave();
  }

  @action
  handleDestinationKeyDown(sb, evt) {
    this._handleDestinationKeyDown(sb, evt);
  }

  @action
  clearInput(sb, evt) {
    evt.stopImmediatePropagation();
    sb.focusInput();
    sb.setInputValue('');
    this.handleClear(sb);
  }

  @action
  preventFocus(evt) {
    evt.preventDefault();
  }

  @action
  handleInputChange(evt) {
    this.onInputChange?.(evt.target.value);
  }

  _search(sb) {
    return this.isCacheHit
      ? Promise.resolve(this.initialOptionsCache)
      : this.optionsProvider?.(this.query, sb);
  }

  _focusInput(sb) {
    // eslint-disable-next-line ember/no-runloop
    next(this, function () {
      sb.focusInput();
    });
  }

  _focusElement(sb) {
    sb.element.focus();
  }

  _setInitialOptionsCache(options) {
    if (this.isCachingEnabled) {
      this.set('initialOptionsCache', options);
    }
  }

  _restoreOptionsFromCache(sb) {
    if (this.isCachingEnabled) {
      this.handleSearched(this.initialOptionsCache, '', sb);
    }
  }
}
